/**
 * SettingsHelpTab
 *
 * The "Help" sub-tab of the Settings page. Primary purpose: a persistent place
 * to (re)launch the Lina AI assistant if the user dismissed her floating popup
 * — dispatches the global `open-lina` event that <LinaAssistant> listens for.
 * Also surfaces a few quick help links.
 */

import { Sparkles, BookOpen, Shield, FileText } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

export function SettingsHelpTab() {
  const { t } = useTranslation();

  const openLina = () => {
    window.dispatchEvent(new CustomEvent('open-lina'));
  };

  const links = [
    {
      icon: BookOpen,
      label: t('settings.help.whitepaper', { defaultValue: 'Whitepaper' }),
      onClick: () => navigateTo('/whitepaper'),
    },
    {
      icon: Shield,
      label: t('settings.help.privacy', { defaultValue: 'Privacy Policy' }),
      onClick: () => navigateTo('/privacy'),
    },
    {
      icon: FileText,
      label: t('settings.help.terms', { defaultValue: 'Terms of Service' }),
      onClick: () => navigateTo('/terms'),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Lina launcher card */}
      <div className="rounded-xl border border-white/10 bg-black/20 p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/20">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-white">
              {t('settings.help.linaTitle', { defaultValue: 'Lina — your AI assistant' })}
            </h3>
            <p className="text-sm text-white/60 mt-1">
              {t('settings.help.linaDescription', {
                defaultValue:
                  'Describe a video in plain language and Lina configures everything for you. She lives as a floating button in the bottom-right corner — reopen her here any time.',
              })}
            </p>
            <button
              onClick={openLina}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium transition-colors min-h-[44px]"
              data-testid="settings-open-lina"
            >
              <Sparkles className="h-4 w-4" />
              {t('settings.help.openLina', { defaultValue: 'Open Lina' })}
            </button>
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {links.map(({ icon: Icon, label, onClick }) => (
          <button
            key={label}
            onClick={onClick}
            className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/10 bg-black/20 hover:bg-white/5 text-white/80 hover:text-white transition-colors min-h-[44px]"
          >
            <Icon className="h-5 w-5 text-primary-400 flex-shrink-0" />
            <span className="text-sm font-medium truncate">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
